/**
 * Centralised, read-only access to the INITIAL server-injected page bootstrap
 * (window.SHVO), i.e. the state snapshot at page load.
 *
 * Note: this is NOT the live runtime state. Later per-component changes go to
 * the store (HelperGlobalDataStore) / core.state, not back into SHVO.pageState,
 * so these helpers always return the initial (bootstrap) snapshot.
 *
 * Prefer these helpers over reaching into `window.SHVO` directly, so access is
 * traceable, mockable in tests and easy to change in a single place.
 */

const getSHVO = () => (typeof window !== 'undefined' ? window.SHVO : undefined);

/**
 * The full initial server-injected page state (SHVO.pageState).
 * @returns {Object|undefined}
 */
export const getInitialPageState = () => getSHVO()?.pageState;

/**
 * The initial componentData block for a single component from the page state.
 * @param {string} name - the componentData key, e.g. 'componentSocials'
 * @returns {Object|undefined}
 */
export const getInitialComponentData = (name) => getInitialPageState()?.componentData?.[name];

/**
 * A single top-level meta field from SHVO (e.g. 'pageLang', 'siteURL', 'profileUrl').
 * @param {string} key
 * @param {*} [fallback]
 * @returns {*}
 */
export const getInitialMeta = (key, fallback = undefined) => getSHVO()?.[key] ?? fallback;
